import { Box, Group, Modal, Stack } from '@mantine/core';
import {
  Button,
  InputAmount,
  InputDay,
  InputSelect,
  InputTextarea,
  InputWrapper,
} from '@vrplatform/components';
import {
  showApiErrorNotification,
  STORYBOOK_BANK_ACCOUNT_SELECT_OPTIONS_QUERY_KEY,
  useApiMutation,
  useBankAccountSelectOptionsQuery,
  useInvalidateApiQueries,
  useTeam,
} from '@vrplatform/data';
import { showSuccessNotification } from '@vrplatform/theme';
import { bankersRoundToCents, day, formatCurrency } from '@vrplatform/utils';
import { useCallback, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import {
  buildAddBankTransferRequestBody,
  buildEditBankTransferRequestBody,
  type EditableBankTransfer,
} from './AddBankTransferModal.utils';

type Props = {
  opened: boolean;
  closeModal: () => void;
  bankTransfer?: EditableBankTransfer | null;
  zIndex?: number;
};

type FormInputs = {
  fromBankAccountId: string | null;
  toBankAccountId: string | null;
  date: string | null;
  amount: number | '';
  description: string;
};

export const STORYBOOK_ADD_BANK_TRANSFER_MODAL_QUERY_KEY =
  STORYBOOK_BANK_ACCOUNT_SELECT_OPTIONS_QUERY_KEY;

const getDefaultValues = (): FormInputs => ({
  fromBankAccountId: null,
  toBankAccountId: null,
  date: day().yyyymmdd(),
  amount: '',
  description: '',
});

export const AddBankTransferModal = ({
  closeModal: cl,
  opened,
  bankTransfer,
  zIndex,
}: Props) => {
  const [{ currency }] = useTeam();
  const goto = useNavigate();
  const isEdit = !!bankTransfer?.id;

  const formValues = useMemo(
    () =>
      bankTransfer
        ? {
            fromBankAccountId: bankTransfer.fromBankAccountId,
            toBankAccountId: bankTransfer.toBankAccountId,
            date: bankTransfer.date,
            amount: Math.abs(bankTransfer.amount || 0),
            description: bankTransfer.description,
          }
        : getDefaultValues(),
    [bankTransfer]
  );
  const methods = useForm<FormInputs>({
    values: formValues,
  });
  const invalidate = useInvalidateApiQueries([
    'bank-transfers',
    'accounts',
    'owner-statements',
  ]);
  const { mutateAsync: create, isPending: isCreating } = useApiMutation(
    'post',
    '/transactions'
  );
  const { mutateAsync: update, isPending: isUpdating } = useApiMutation(
    'put',
    '/transactions/{id}'
  );
  const isPending = isCreating || isUpdating;

  const { data: bankAccountOptions = [], isLoading: isLoadingBankAccounts } =
    useBankAccountSelectOptionsQuery({
      skip: !opened,
    });

  const closeModal = useCallback(() => {
    methods.reset(formValues);
    cl();
  }, [cl, methods, formValues]);

  const bankAccountOptionsById = useMemo(
    () => new Map(bankAccountOptions.map((option) => [option.value, option])),
    [bankAccountOptions]
  );

  const submit = async (data: FormInputs) => {
    try {
      if (
        !data.fromBankAccountId ||
        !data.toBankAccountId ||
        !data.date ||
        typeof data.amount !== 'number'
      ) {
        return;
      }

      const description = data.description?.trim() || undefined;

      if (isEdit) {
        if (!bankTransfer?.lineId) {
          throw new Error('Missing transfer line id');
        }

        await update({
          params: {
            path: {
              id: bankTransfer.id,
            },
          },
          body: buildEditBankTransferRequestBody({
            lineId: bankTransfer.lineId,
            fromBankAccountId: data.fromBankAccountId,
            toBankAccountId: data.toBankAccountId,
            date: data.date,
            amount: data.amount,
            description,
          }),
        });
      } else {
        await create({
          body: buildAddBankTransferRequestBody({
            fromBankAccountId: data.fromBankAccountId,
            toBankAccountId: data.toBankAccountId,
            date: data.date,
            amount: data.amount,
            description,
          }),
        });
      }

      invalidate();
      closeModal();
      if (!isEdit) {
        goto('/reconciliation/transfers');
      }

      showSuccessNotification({
        title: 'Success!',
        message: isEdit
          ? 'Successfully updated bank transfer.'
          : 'The bank transfer was successfully created.',
      });
    } catch (error) {
      showApiErrorNotification({
        error,
        title: isEdit
          ? 'Failed to update bank transfer'
          : 'Failed to create bank transfer',
        defaultMessage:
          'We failed to save the bank transfer. Please try again and contact support if the issue persists.',
      });
    }
  };

  return (
    <Modal
      centered
      radius="md"
      opened={opened}
      onClose={closeModal}
      zIndex={zIndex}
      title={isEdit ? 'Edit bank transfer' : 'Add bank transfer'}
      size={520}
      styles={{
        title: {
          fontWeight: 500,
        },
      }}
    >
      <Box
        component="form"
        onReset={closeModal}
        onSubmit={methods.handleSubmit(submit)}
      >
        <Stack mb="xl">
          <Controller
            control={methods.control}
            name="fromBankAccountId"
            rules={{
              required: 'Please select a bank account',
              validate: (value) =>
                value !== methods.getValues('toBankAccountId') ||
                'Please select a different bank account',
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label="From bank account"
                error={fieldState.error?.message}
                required
              >
                <InputSelect
                  type="single"
                  data={{
                    options: bankAccountOptions,
                    loading: isLoadingBankAccounts,
                  }}
                  value={
                    field.value
                      ? (bankAccountOptionsById.get(field.value) ?? null)
                      : null
                  }
                  setValue={(value) => field.onChange(value?.value || null)}
                  inputProps={{
                    placeholder: 'Select bank account',
                    error: !!fieldState.error,
                  }}
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={methods.control}
            name="toBankAccountId"
            rules={{
              required: 'Please select a bank account',
              validate: (value) =>
                value !== methods.getValues('fromBankAccountId') ||
                'Please select a different bank account',
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label="To bank account"
                error={fieldState.error?.message}
                required
              >
                <InputSelect
                  type="single"
                  data={{
                    options: bankAccountOptions,
                    loading: isLoadingBankAccounts,
                  }}
                  value={
                    field.value
                      ? (bankAccountOptionsById.get(field.value) ?? null)
                      : null
                  }
                  setValue={(value) => field.onChange(value?.value || null)}
                  inputProps={{
                    placeholder: 'Select bank account',
                    error: !!fieldState.error,
                  }}
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={methods.control}
            name="date"
            rules={{
              required: 'Please enter a date',
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label="Date"
                error={fieldState.error?.message}
                required
              >
                <InputDay
                  value={field.value ? day(field.value).toDate() : null}
                  onChange={(value) =>
                    field.onChange(value ? day(value).yyyymmdd() : null)
                  }
                  clearable
                  error={!!fieldState.error}
                  placeholder={day().format('LL')}
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={methods.control}
            name="amount"
            rules={{
              validate: (value) => {
                if (typeof value !== 'number') {
                  return 'Please enter an amount';
                }

                if (value <= 0) {
                  return 'Please enter an amount greater than zero';
                }

                return true;
              },
            }}
            render={({ field, fieldState }) => (
              <InputWrapper
                label="Amount"
                error={fieldState.error?.message}
                required
              >
                <InputAmount
                  value={
                    typeof field.value === 'number'
                      ? field.value / 100
                      : field.value
                  }
                  setValue={(value) => {
                    if (typeof value === 'string') {
                      if (value === '') {
                        field.onChange('');
                        return;
                      }

                      const bankers = bankersRoundToCents(value);
                      if (bankers === field.value) return;
                    }

                    field.onChange(bankersRoundToCents(value));
                  }}
                  error={!!fieldState.error}
                  decimalScale={2}
                  placeholder={formatCurrency(0, currency)}
                />
              </InputWrapper>
            )}
          />

          <Controller
            control={methods.control}
            name="description"
            render={({ field, fieldState }) => (
              <InputWrapper
                label="Description"
                error={fieldState.error?.message}
              >
                <InputTextarea
                  onChange={field.onChange}
                  value={field.value}
                  onBlur={field.onBlur}
                  height={100}
                  error={!!fieldState.error}
                  placeholder="Add description ..."
                  name={field.name}
                  aria-label={field.name}
                />
              </InputWrapper>
            )}
          />
        </Stack>

        <Group justify="flex-end">
          <Button type="reset" disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={isPending}>
            {isEdit ? 'Save changes' : 'Add transfer'}
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
